import { apiClient } from '@shared/api';
import type { ApiResponse } from '@shared/types';
import type {
  MroDashboardSummaryDto,
  RecentMrsDto,
  TopRequestorDto,
  LowStockItemDto,
  FastMovingItemDto,
  TopRequestedItemDto,
  RequestAgingDto,
  MovementTrendDto,
  InventoryMovementDto,
  DashboardQueryParams,
} from '../types';

class MroDashboardService {
  private readonly baseUrl = '/mro-dashboard';

  async getSummary(params?: DashboardQueryParams): Promise<MroDashboardSummaryDto> {
    const { data } = await apiClient.get<ApiResponse<MroDashboardSummaryDto>>(
      `${this.baseUrl}/summary`,
      { params }
    );

    if (data.success && data.data) {
      return data.data;
    }

    throw new Error(data.message || 'Failed to fetch dashboard summary');
  }

  async getRecentMrs(limit = 20): Promise<RecentMrsDto[]> {
    const { data } = await apiClient.get<ApiResponse<RecentMrsDto[]>>(
      `${this.baseUrl}/recent-mrs`,
      { params: { limit } }
    );

    if (data.success && data.data) {
      return data.data;
    }

    throw new Error(data.message || 'Failed to fetch recent MRS');
  }

  async getRecentMovements(limit = 20): Promise<InventoryMovementDto[]> {
    const { data } = await apiClient.get<ApiResponse<InventoryMovementDto[]>>(
      `${this.baseUrl}/movements`,
      { params: { limit } }
    );

    if (data.success && data.data) {
      return data.data;
    }

    throw new Error(data.message || 'Failed to fetch recent movements');
  }

  async getTopRequestors(params?: DashboardQueryParams): Promise<TopRequestorDto[]> {
    const { data } = await apiClient.get<ApiResponse<TopRequestorDto[]>>(
      `${this.baseUrl}/top-requestors`,
      { params }
    );

    if (data.success && data.data) {
      return data.data;
    }

    throw new Error(data.message || 'Failed to fetch top requestors');
  }

  async getLowStockItems(threshold = 10): Promise<LowStockItemDto[]> {
    const { data } = await apiClient.get<ApiResponse<LowStockItemDto[]>>(
      `${this.baseUrl}/low-stock`,
      { params: { threshold } }
    );

    if (data.success && data.data) {
      return data.data;
    }

    throw new Error(data.message || 'Failed to fetch low stock items');
  }

  async getFastMovingItems(params?: DashboardQueryParams): Promise<FastMovingItemDto[]> {
    const { data } = await apiClient.get<ApiResponse<FastMovingItemDto[]>>(
      `${this.baseUrl}/fast-moving`,
      { params }
    );

    if (data.success && data.data) {
      return data.data;
    }

    throw new Error(data.message || 'Failed to fetch fast moving items');
  }

  async getTopRequestedItems(params?: DashboardQueryParams): Promise<TopRequestedItemDto[]> {
    const { data } = await apiClient.get<ApiResponse<TopRequestedItemDto[]>>(
      `${this.baseUrl}/top-items`,
      { params }
    );

    if (data.success && data.data) {
      return data.data;
    }

    throw new Error(data.message || 'Failed to fetch top requested items');
  }

  async getRequestAging(): Promise<RequestAgingDto[]> {
    const { data } = await apiClient.get<ApiResponse<RequestAgingDto[]>>(
      `${this.baseUrl}/request-aging`
    );

    if (data.success && data.data) {
      return data.data;
    }

    throw new Error(data.message || 'Failed to fetch request aging');
  }

  async getMovementTrends(params?: DashboardQueryParams): Promise<MovementTrendDto[]> {
    const { data } = await apiClient.get<ApiResponse<MovementTrendDto[]>>(
      `${this.baseUrl}/movement-trends`,
      { params }
    );

    if (data.success && data.data) {
      return data.data;
    }

    throw new Error(data.message || 'Failed to fetch movement trends');
  }
}

export const mroDashboardService = new MroDashboardService();
